import React, { useState, useEffect, useRef, useCallback } from "react";
import Grid from "@mui/material/Unstable_Grid2";
import {
  Box,
  Typography,
  IconButton,
  Button,
  Chip,
  LinearProgress,
  Tooltip,
  Paper,
  Fade,
  Zoom,
  Avatar,
} from "@mui/material";
import {
  Close,
  NavigateNext,
  NavigateBefore,
  SmartToy,
  PlayArrow,
  Pause,
  Replay,
  CheckCircle,
  ArrowBack,
  Groups,
  Refresh,
  HelpOutline,
  CompareArrows,
  CheckCircleOutline,
  CancelOutlined,
  Merge,
  RestartAlt,
  DoneAll,
  Undo,
  Fullscreen,
  Person,
  Delete,
  FiberManualRecord,
} from "@mui/icons-material";

// ─── TOUR STEPS — exactly matching TeamCollaboration.jsx elements ─────────────
const TOUR_STEPS = [
  {
    id: "welcome",
    title: "Welcome to Team Collaboration! 🤝",
    description:
      "Hi! I'm your Team Collaboration guide. I'll walk you through every feature on this screen so your whole team knows how to collaborate, compare, and merge documents effectively. Let's go!",
    icon: <SmartToy />,
    color: "#6366f1",
    highlight: null,
    category: "intro",
  },
  {
    id: "back-btn",
    title: "Back to My Documents",
    description:
      "The back arrow (top-left) navigates you back to My Documents. All accepted, rejected, or merged changes are preserved — nothing is lost when you navigate away.",
    icon: <ArrowBack />,
    color: "#1976d2",
    highlight: "tour-back-btn",
    category: "header",
  },
  {
    id: "help-btn",
    title: "Help / Feature Guide",
    description:
      "The ? Help button launches the ReactTour inline guide — it highlights each section in sequence with an overlay tooltip explaining what it does. Great for first-time users.",
    icon: <HelpOutline />,
    color: "#0288d1",
    highlight: "tour-help-btn",
    category: "header",
  },
  {
    id: "refresh-btn",
    title: "Refresh Collaboration",
    description:
      "Pulls the latest collaborator version from the server. Use this when a teammate just submitted their changes and you want the freshest diff without a full page reload.",
    icon: <Refresh />,
    color: "#ed6c02",
    highlight: "tour-refresh-btn",
    category: "header",
  },
  {
    id: "collaborators-btn",
    title: "Collaborators Button",
    description:
      "Opens the Collaborators modal listing everyone on this document. Click any collaborator card to load their version for comparison. The trash icon permanently removes a collaborator.",
    icon: <Groups />,
    color: "#1976d2",
    highlight: "tour-collaborators-btn",
    category: "header",
  },
  {
    id: "doc-title-card",
    title: "Document Info Card",
    description:
      "Shows the document name, who you are currently comparing with, an Owner chip, total collaborator count, and a live counter showing how many blocks you have already reviewed.",
    icon: <CompareArrows />,
    color: "#1976d2",
    highlight: "tour-doc-title-card",
    category: "overview",
  },
  {
    id: "owner-column",
    title: "Owner Column — Your Document",
    description:
      "The left column is YOUR current document version. Green highlighted words are new content added by the collaborator. Red highlighted words are content that would be removed if you accept their change.",
    icon: <Person />,
    color: "#1976d2",
    highlight: "tour-owner-column",
    category: "diff",
  },
  {
    id: "owner-fullscreen",
    title: "Owner Fullscreen View",
    description:
      "The fullscreen icon on the Owner column header expands your document to fill the entire screen. The action column and collaborator column are hidden for distraction-free reading.",
    icon: <Fullscreen />,
    color: "#1976d2",
    highlight: "tour-owner-fullscreen",
    category: "diff",
  },
  {
    id: "collab-tools",
    title: "Collaboration Tools Panel",
    description:
      "The center column is the action hub. For every paragraph block that differs between your version and the collaborator's, a card appears here with Accept, Reject, and Merge Both buttons.",
    icon: <CompareArrows />,
    color: "#1976d2",
    highlight: "tour-collab-tools",
    category: "actions",
  },
  {
    id: "accept-btn",
    title: "Accept Change ✅",
    description:
      "Replaces your paragraph block with the collaborator's version. The block turns green in your document to confirm the acceptance. An Undo button appears immediately if you change your mind.",
    icon: <CheckCircleOutline />,
    color: "#4caf50",
    highlight: "tour-accept-btn",
    category: "actions",
  },
  {
    id: "reject-btn",
    title: "Reject Change ❌",
    description:
      "Keeps your original paragraph and marks the collaborator's suggestion as rejected. The block turns red in your document to confirm the rejection was recorded.",
    icon: <CancelOutlined />,
    color: "#f44336",
    highlight: "tour-reject-btn",
    category: "actions",
  },
  {
    id: "merge-btn",
    title: "Merge Both 🔀",
    description:
      "Appends the collaborator's content to the END of your existing paragraph block, keeping both versions. Perfect when the collaborator added extra context you also want to preserve.",
    icon: <Merge />,
    color: "#2196f3",
    highlight: "tour-merge-btn",
    category: "actions",
  },
  {
    id: "undo-btn",
    title: "Undo Change ↩",
    description:
      "After accepting, rejecting, or merging, an Undo button replaces the action buttons for that block. Click it to restore the block to its original unreviewed state — the change counter decrements automatically.",
    icon: <Undo />,
    color: "#ff9800",
    highlight: "tour-undo-btn",
    category: "actions",
  },
  {
    id: "collab-column",
    title: "Collaborator Column",
    description:
      "The right column shows the collaborator's submitted version. Character-level diff highlighting lets you see exactly what they changed compared to your original text.",
    icon: <Groups />,
    color: "#9c27b0",
    highlight: "tour-collab-column",
    category: "diff",
  },
  {
    id: "collab-fullscreen",
    title: "Collaborator Fullscreen View",
    description:
      "The fullscreen icon on the Collaborator column header expands their document to full screen. Great for reading long documents before deciding whether to accept, reject, or merge changes.",
    icon: <Fullscreen />,
    color: "#9c27b0",
    highlight: "tour-collab-fullscreen",
    category: "diff",
  },
  {
    id: "reset-btn",
    title: "Reset All Changes",
    description:
      "Reverts every accepted, rejected, and merged decision back to the original unseen state. The change counter resets to 0. Use this to start your entire review completely from scratch.",
    icon: <RestartAlt />,
    color: "#ff5722",
    highlight: "tour-reset-btn",
    category: "footer",
  },
  {
    id: "changes-chip",
    title: "Changes Counter",
    description:
      "Shows how many blocks you have reviewed so far. It increases with each Accept / Reject / Merge action and decreases when you Undo. The Complete button stays disabled until this is at least 1.",
    icon: <CompareArrows />,
    color: "#1976d2",
    highlight: "tour-changes-chip",
    category: "footer",
  },
  {
    id: "complete-btn",
    title: "Complete Collaboration",
    description:
      "Saves all reviewed changes permanently to the main document and removes the collaborator's draft. A confirmation dialog appears first — this action cannot be undone after confirming.",
    icon: <DoneAll />,
    color: "#009688",
    highlight: "tour-complete-btn",
    category: "footer",
  },
  {
    id: "modal",
    title: "Collaborators Modal",
    description:
      "Click the Collaborators button to open this modal. Each card shows a collaborator — click a card to load their version into the diff view. The red trash icon permanently removes them from the document.",
    icon: <Delete />,
    color: "#d32f2f",
    highlight: "tour-collaborators-btn",
    category: "modal",
  },
  {
    id: "done",
    title: "You're ready to collaborate! 🚀",
    description:
      "That completes the full Team Collaboration tour! Accept, reject, or merge each block, then click Complete Collaboration when you are done. Relaunch this tour anytime from the bot button.",
    icon: <SmartToy />,
    color: "#6366f1",
    highlight: null,
    category: "intro",
  },
];

const CATEGORY_META = {
  intro:    { label: "Intro",    color: "#6366f1" },
  header:   { label: "Header",   color: "#1976d2" },
  overview: { label: "Overview", color: "#0288d1" },
  diff:     { label: "Diff",     color: "#9c27b0" },
  actions:  { label: "Actions",  color: "#4caf50" },
  footer:   { label: "Footer",   color: "#009688" },
  modal:    { label: "Modal",    color: "#d32f2f" },
};

// ─── Spotlight Overlay ────────────────────────────────────────────────────────
function SpotlightOverlay({ targetId, color }) {
  const [rect, setRect] = useState(null);

  useEffect(() => {
    if (!targetId) { setRect(null); return; }
    const el = document.getElementById(targetId);
    if (!el) { setRect(null); return; }
    el.scrollIntoView({ behavior: "smooth", block: "nearest" });
    const t = setTimeout(() => {
      const r = el.getBoundingClientRect();
      setRect({ left: r.left, top: r.top, width: r.width, height: r.height });
    }, 320);
    return () => clearTimeout(t);
  }, [targetId]);

  if (!rect) return null;
  const PAD = 10;

  return (
    <Box sx={{ position: "fixed", inset: 0, zIndex: 1299, pointerEvents: "none" }}>
      <svg width="100%" height="100%" style={{ position: "absolute", inset: 0 }}>
        <defs>
          <mask id="tc-mask">
            <rect width="100%" height="100%" fill="white" />
            <rect
              x={rect.left - PAD} y={rect.top - PAD}
              width={rect.width + PAD * 2} height={rect.height + PAD * 2}
              rx="10" fill="black"
            />
          </mask>
        </defs>
        <rect width="100%" height="100%" fill="rgba(0,0,0,0.52)" mask="url(#tc-mask)" />
        <rect
          x={rect.left - PAD} y={rect.top - PAD}
          width={rect.width + PAD * 2} height={rect.height + PAD * 2}
          rx="10" fill="none" stroke={color} strokeWidth="2.5"
          style={{ filter: `drop-shadow(0 0 12px ${color})` }}
        />
      </svg>
    </Box>
  );
}

// ─── Animated Bot Avatar ──────────────────────────────────────────────────────
function BotAvatar({ color, isTalking }) {
  return (
    <Box sx={{ position: "relative", width: 56, height: 56, flexShrink: 0 }}>
      <Box sx={{
        position: "absolute", inset: -5, borderRadius: "50%",
        border: `2px solid ${color}`,
        animation: "tcPulse 1.8s ease-out infinite",
        "@keyframes tcPulse": {
          "0%": { transform: "scale(1)", opacity: 0.8 },
          "100%": { transform: "scale(1.6)", opacity: 0 },
        },
      }} />
      <Avatar sx={{
        width: 56, height: 56,
        background: `linear-gradient(135deg, ${color}, ${color}88)`,
        border: `2.5px solid ${color}`,
        boxShadow: `0 0 22px ${color}55`,
        animation: isTalking
          ? "tcBob 0.5s ease-in-out infinite alternate"
          : "tcFloat 3s ease-in-out infinite",
        "@keyframes tcBob": {
          "0%": { transform: "translateY(0) scale(1)" },
          "100%": { transform: "translateY(-5px) scale(1.07)" },
        },
        "@keyframes tcFloat": {
          "0%,100%": { transform: "translateY(0)" },
          "50%": { transform: "translateY(-8px)" },
        },
      }}>
        <SmartToy sx={{ fontSize: 30, color: "#fff" }} />
      </Avatar>
      {isTalking && (
        <Box sx={{
          position: "absolute", bottom: -13, left: "50%",
          transform: "translateX(-50%)", display: "flex", gap: "3px",
        }}>
          {[0, 1, 2].map((i) => (
            <Box key={i} sx={{
              width: 5, height: 5, borderRadius: "50%", bgcolor: color,
              animation: `tcDot 0.8s ease-in-out ${i * 0.16}s infinite`,
              "@keyframes tcDot": {
                "0%,100%": { transform: "translateY(0)" },
                "50%": { transform: "translateY(-6px)" },
              },
            }} />
          ))}
        </Box>
      )}
    </Box>
  );
}

// ─── Category Mini-Map ────────────────────────────────────────────────────────
function MiniMap({ steps, currentIdx, onJump }) {
  const cats = [...new Set(steps.map((s) => s.category))];
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.65, mt: 1.5 }}>
      {cats.map((cat) => {
        const meta = CATEGORY_META[cat];
        const catSteps = steps.filter((s) => s.category === cat);
        return (
          <Box key={cat} sx={{ display: "flex", alignItems: "center", gap: 0.7 }}>
            <Typography sx={{
              fontSize: 7.5, fontWeight: 800, color: meta.color,
              textTransform: "uppercase", letterSpacing: "0.07em",
              minWidth: 48, lineHeight: 1,
            }}>
              {meta.label}
            </Typography>
            <Box sx={{ display: "flex", gap: "3px", flexWrap: "wrap" }}>
              {catSteps.map((step) => {
                const idx = steps.indexOf(step);
                const isActive = idx === currentIdx;
                const isDone = idx < currentIdx;
                return (
                  <Tooltip key={step.id} title={step.title} placement="top" arrow>
                    <Box
                      onClick={() => onJump(idx)}
                      sx={{
                        width: isActive ? 20 : 7, height: 7,
                        borderRadius: 4, cursor: "pointer",
                        bgcolor: isActive
                          ? meta.color
                          : isDone ? `${meta.color}77` : `${meta.color}22`,
                        transition: "all 0.3s",
                        "&:hover": { transform: "scale(1.5)" },
                      }}
                    />
                  </Tooltip>
                );
              })}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}

// ─── Main TourBot ─────────────────────────────────────────────────────────────
export default function TeamCollaborationTourBot() {
  const [isOpen, setIsOpen]       = useState(false);
  const [step, setStep]           = useState(0);
  const [playing, setPlaying]     = useState(false);
  const [talking, setTalking]     = useState(false);
  const [displayed, setDisplayed] = useState("");
  const [typing, setTyping]       = useState(false);
  const typeTimer = useRef(null);
  const playTimer = useRef(null);

  const current  = TOUR_STEPS[step];
  const isLast   = step === TOUR_STEPS.length - 1;
  const isFirst  = step === 0;
  const progress = ((step + 1) / TOUR_STEPS.length) * 100;

  const typeText = useCallback((text) => {
    setDisplayed("");
    setTyping(true);
    setTalking(true);
    let i = 0;
    clearInterval(typeTimer.current);
    typeTimer.current = setInterval(() => {
      if (i <= text.length) { setDisplayed(text.slice(0, i)); i++; }
      else {
        clearInterval(typeTimer.current);
        setTyping(false);
        setTalking(false);
      }
    }, 14);
  }, []);

  useEffect(() => {
    if (isOpen) typeText(current.description);
    return () => clearInterval(typeTimer.current);
  }, [step, isOpen]);

  useEffect(() => {
    if (playing && !typing) {
      playTimer.current = setTimeout(() => {
        if (step < TOUR_STEPS.length - 1) setStep((p) => p + 1);
        else setPlaying(false);
      }, 3800);
    }
    return () => clearTimeout(playTimer.current);
  }, [playing, step, typing]);

  const next    = () => { if (!isLast) setStep((p) => p + 1); };
  const prev    = () => { if (!isFirst) setStep((p) => p - 1); };
  const restart = () => { setStep(0); setPlaying(false); };
  const close   = () => { setIsOpen(false); setPlaying(false); setStep(0); };

  return (
    <>
      {isOpen && <SpotlightOverlay targetId={current.highlight} color={current.color} />}

      {/* ── Floating launch button ── */}
      {!isOpen && (
        <Zoom in>
          <Box sx={{ position: "fixed", bottom: 95, right: 30, zIndex: 1400 }}>
            <Tooltip title="Start Feature Tour" placement="left" arrow>
              <Box
                onClick={() => setIsOpen(true)}
                sx={{
                  width: 64, height: 64, borderRadius: "50%",
                  background: "linear-gradient(135deg,#1976d2,#009688)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  cursor: "pointer",
                  boxShadow: "0 6px 28px rgba(25,118,210,0.55)",
                  animation: "tcLaunch 3s ease-in-out infinite",
                  "@keyframes tcLaunch": {
                    "0%,100%": { transform: "translateY(0)" },
                    "50%": { transform: "translateY(-10px)" },
                  },
                  "&:hover": { transform: "scale(1.12)", transition: "transform 0.2s" },
                }}
              >
                <SmartToy sx={{ color: "#fff", fontSize: 32 }} />
              </Box>
            </Tooltip>
            <Typography sx={{
              position: "absolute", bottom: -20, left: "50%",
              transform: "translateX(-50%)",
              fontSize: 8.5, fontWeight: 800, color: "#1976d2",
              whiteSpace: "nowrap", letterSpacing: "0.08em",
            }}>
              FEATURE TOUR
            </Typography>
          </Box>
        </Zoom>
      )}

      {/* ── Tour Panel ── */}
      {isOpen && (
        <Fade in>
          <Box sx={{
            position: "fixed", bottom: 95, left: "50%",
            transform: "translateX(-50%)",
            width: { xs: "96vw", sm: 720 },
            zIndex: 1400,
          }}>
            <Paper elevation={0} sx={{
              borderRadius: "18px", overflow: "hidden",
              border: `1px solid ${current.color}28`,
              boxShadow: `0 20px 60px rgba(0,0,0,0.22), 0 0 0 1px ${current.color}18`,
              bgcolor: "rgba(255,255,255,0.97)",
              backdropFilter: "blur(20px)",
            }}>
              {/* Progress bar */}
              <LinearProgress
                variant="determinate" value={progress}
                sx={{
                  height: 3,
                  bgcolor: `${current.color}18`,
                  "& .MuiLinearProgress-bar": {
                    bgcolor: current.color,
                    transition: "width 0.55s ease",
                  },
                }}
              />

              <Grid container sx={{ p: "20px 22px 18px" }}>
                {/* LEFT: avatar + minimap */}
                <Grid xs={12} sm="auto" sx={{
                  display: "flex", flexDirection: "column", alignItems: "center",
                  pr: { sm: 2.5 }, mb: { xs: 2, sm: 0 }, minWidth: { sm: 122 },
                }}>
                  <BotAvatar color={current.color} isTalking={talking} />
                  <MiniMap steps={TOUR_STEPS} currentIdx={step} onJump={setStep} />
                </Grid>

                {/* RIGHT: content */}
                <Grid xs={12} sm sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                  {/* Title row */}
                  <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                      <Box sx={{
                        p: 0.85, borderRadius: "10px",
                        bgcolor: `${current.color}14`, color: current.color,
                        display: "flex",
                        boxShadow: `inset 0 0 0 1px ${current.color}22`,
                      }}>
                        {React.cloneElement(current.icon, { sx: { fontSize: 22 } })}
                      </Box>
                      <Box>
                        <Typography sx={{
                          fontWeight: 700, fontSize: 15.5, color: "#0f172a",
                          lineHeight: 1.25, letterSpacing: "-0.01em",
                        }}>
                          {current.title}
                        </Typography>
                        <Chip
                          label={CATEGORY_META[current.category].label}
                          size="small"
                          sx={{
                            height: 18, fontSize: 9, fontWeight: 800, mt: 0.4,
                            bgcolor: `${CATEGORY_META[current.category].color}12`,
                            color: CATEGORY_META[current.category].color,
                            border: `1px solid ${CATEGORY_META[current.category].color}38`,
                            letterSpacing: "0.04em",
                          }}
                        />
                      </Box>
                    </Box>
                    <IconButton size="small" onClick={close} sx={{
                      bgcolor: "#fef2f2", color: "#dc2626",
                      width: 28, height: 28, border: "1px solid #fecaca",
                      "&:hover": { bgcolor: "#dc2626", color: "#fff" },
                      transition: "all 0.2s", flexShrink: 0,
                    }}>
                      <Close sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Box>

                  {/* Description */}
                  <Box sx={{
                    p: "12px 14px", bgcolor: "#f8fafc",
                    borderRadius: "10px", border: "1px solid #e2e8f0", minHeight: 68,
                  }}>
                    <Typography sx={{ fontSize: 13, color: "#475569", lineHeight: 1.78 }}>
                      {displayed}
                      {typing && (
                        <Box component="span" sx={{
                          display: "inline-block", width: "2px", height: "13px",
                          bgcolor: current.color, ml: "1px", verticalAlign: "middle",
                          animation: "tcBlink 0.7s step-end infinite",
                          "@keyframes tcBlink": {
                            "0%,100%": { opacity: 1 }, "50%": { opacity: 0 },
                          },
                        }} />
                      )}
                    </Typography>
                  </Box>

                  {/* Controls */}
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      <FiberManualRecord sx={{ fontSize: 7, color: current.color }} />
                      <Typography sx={{ fontSize: 11, color: "#94a3b8", fontWeight: 600 }}>
                        {step + 1} / {TOUR_STEPS.length}
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", gap: 0.7, alignItems: "center" }}>
                      <Tooltip title="Restart" arrow>
                        <IconButton size="small" onClick={restart} sx={{
                          color: "#94a3b8", width: 30, height: 30,
                          "&:hover": { color: current.color, bgcolor: `${current.color}12` },
                        }}>
                          <Replay sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={playing ? "Pause" : "Auto-play"} arrow>
                        <IconButton size="small" onClick={() => setPlaying((p) => !p)} sx={{
                          width: 30, height: 30,
                          bgcolor: playing ? `${current.color}18` : "transparent",
                          color: current.color,
                          border: `1px solid ${current.color}44`,
                          borderRadius: "8px",
                          "&:hover": { bgcolor: `${current.color}28` },
                        }}>
                          {playing ? <Pause sx={{ fontSize: 16 }} /> : <PlayArrow sx={{ fontSize: 16 }} />}
                        </IconButton>
                      </Tooltip>
                      <Button size="small" onClick={prev} disabled={isFirst}
                        startIcon={<NavigateBefore sx={{ fontSize: "16px !important" }} />}
                        sx={{
                          borderRadius: "8px", textTransform: "none",
                          fontSize: 12, color: "#64748b", px: 1.5,
                          "&:disabled": { opacity: 0.3 },
                          "&:hover": { bgcolor: "#f1f5f9" },
                        }}>
                        Back
                      </Button>
                      <Button size="small"
                        onClick={isLast ? close : next}
                        endIcon={isLast
                          ? <CheckCircle sx={{ fontSize: "16px !important" }} />
                          : <NavigateNext sx={{ fontSize: "16px !important" }} />
                        }
                        variant="contained"
                        sx={{
                          borderRadius: "8px", textTransform: "none",
                          fontSize: 12, px: 2, fontWeight: 600,
                          bgcolor: current.color,
                          boxShadow: `0 3px 12px ${current.color}44`,
                          "&:hover": {
                            bgcolor: current.color,
                            transform: "translateY(-1px)",
                            boxShadow: `0 6px 18px ${current.color}55`,
                          },
                          transition: "all 0.2s",
                        }}>
                        {isLast ? "Done!" : "Next"}
                      </Button>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </Paper>
          </Box>
        </Fade>
      )}
    </>
  );
}